import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";

const FORMSPREE = "https://formspree.io/f/xljdzpzz";
const BETA_START = "November 15, 2026";

const fieldClass =
  "w-full rounded-md border border-[#C4A36A]/45 bg-[#FBF6EC] px-4 py-3 font-serif text-[15px] text-[#1A120C] caret-[#1A120C] outline-none placeholder:text-[#8A7E70] focus:border-[#C4A36A] focus:ring-1 focus:ring-[#C4A36A]/30 [&:-webkit-autofill]:[-webkit-text-fill-color:#1A120C] [&:-webkit-autofill]:[box-shadow:0_0_0_1000px_#FBF6EC_inset]";
const buttonClass =
  "inline-flex h-11 items-center justify-center gap-2 rounded-full bg-[#C4A36A] px-8 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#FFF8E8] transition-colors hover:bg-[#B0894C] disabled:cursor-wait";

const LeafSprig = ({ className = "h-4 w-6" }) => (
  <svg viewBox="0 0 28 16" className={className} fill="none" aria-hidden="true">
    <path d="M2 13c7.5-1.2 14-6.4 24-11" stroke="currentColor" strokeWidth="1" />
    <path d="M9 12.2c1.2-3.2 4.2-5.4 8.4-6.4" stroke="currentColor" strokeWidth="1" />
    <path d="M14.2 9.4c.9-2.2 2.8-3.6 5.6-4.2" stroke="currentColor" strokeWidth="1" />
    <path d="M18.6 4.2c.2 1.4-.4 2.6-1.6 3.4" stroke="currentColor" strokeWidth="0.9" />
    <path d="M12.4 7.6c.1 1.2-.5 2.2-1.5 2.8" stroke="currentColor" strokeWidth="0.9" />
  </svg>
);

const CardCorner = ({ className }) => (
  <svg viewBox="0 0 28 28" className={`h-6 w-6 text-[#C4A36A] ${className}`} fill="none" aria-hidden="true">
    <path d="M2 18 V8 C2 4 4 2 8 2 H18" stroke="currentColor" strokeWidth="1" />
    <path d="M7 7c2.4-.2 4.4-1.6 6-4" stroke="currentColor" strokeWidth="0.85" />
    <path d="M7 7c.2 2.4 1.6 4.4 4 6" stroke="currentColor" strokeWidth="0.85" />
  </svg>
);

const CreamCard = ({ children, className = "", testId }) => (
  <div
    className={`relative rounded-2xl border border-[#C4A36A]/40 bg-[#FBF6EC] px-6 py-7 sm:px-8 sm:py-8 ${className}`}
    data-testid={testId}
  >
    <CardCorner className="pointer-events-none absolute left-2.5 top-2.5" />
    <CardCorner className="pointer-events-none absolute right-2.5 top-2.5 rotate-90" />
    <CardCorner className="pointer-events-none absolute bottom-2.5 left-2.5 -rotate-90" />
    <CardCorner className="pointer-events-none absolute bottom-2.5 right-2.5 rotate-180" />
    {children}
  </div>
);

const KickerRule = () => (
  <svg viewBox="0 0 280 18" className="mx-auto mt-4 h-4 w-64 text-[#C4A36A]" fill="none" aria-hidden="true">
    <path d="M8 9 H112" stroke="currentColor" strokeWidth="0.8" />
    <path d="M168 9 H272" stroke="currentColor" strokeWidth="0.8" />
    <path d="M128 9c8-4 16-4 24 0" stroke="currentColor" strokeWidth="0.8" />
    <path d="M128 9c8 4 16 4 24 0" stroke="currentColor" strokeWidth="0.8" />
    <path d="M140 9l6-3.6 6 3.6-6 3.6-6-3.6Z" fill="currentColor" />
  </svg>
);

const FloralDivider = () => (
  <svg viewBox="0 0 320 36" className="mx-auto my-10 h-9 w-72 text-[#C4A36A]" fill="none" aria-hidden="true">
    <path d="M12 22 H118" stroke="currentColor" strokeWidth="0.8" />
    <path d="M202 22 H308" stroke="currentColor" strokeWidth="0.8" />
    <path d="M118 22c8-1 14-6 22-12" stroke="currentColor" strokeWidth="0.9" />
    <path d="M202 22c-8-1-14-6-22-12" stroke="currentColor" strokeWidth="0.9" />
    <path d="M132 16c2-5 7-8 13-9" stroke="currentColor" strokeWidth="0.85" />
    <path d="M188 16c-2-5-7-8-13-9" stroke="currentColor" strokeWidth="0.85" />
    <path d="M148 22c4-7 10-10 18-10 8 0 14 3 18 10" stroke="currentColor" strokeWidth="0.9" />
    <path d="M160 22c2.5-3.4 5.5-3.4 8 0" stroke="currentColor" strokeWidth="0.8" />
    <circle cx="164" cy="12" r="1.2" fill="currentColor" />
  </svg>
);

const CornerVine = ({ className, flip }) => (
  <svg
    viewBox="0 0 140 180"
    className={`pointer-events-none absolute h-40 w-28 text-[#C4A36A]/70 sm:h-52 sm:w-36 ${className}`}
    fill="none"
    aria-hidden="true"
    style={flip ? { transform: "scaleX(-1)" } : undefined}
  >
    <path d="M18 168c8-28 14-52 16-78 2-28 12-52 42-70 8-5 22-12 40-16" stroke="currentColor" strokeWidth="1" />
    <path d="M38 148c6-10 16-16 28-16" stroke="currentColor" strokeWidth="0.9" />
    <path d="M42 118c8-8 18-10 28-6" stroke="currentColor" strokeWidth="0.9" />
    <path d="M48 88c6-12 16-18 28-16" stroke="currentColor" strokeWidth="0.9" />
    <path d="M62 58c8-10 20-14 32-10" stroke="currentColor" strokeWidth="0.9" />
    <path d="M86 34c8-8 18-10 28-6" stroke="currentColor" strokeWidth="0.9" />
    <path d="M34 152c-1.5-6 1-11 6-14" stroke="currentColor" strokeWidth="0.8" />
    <path d="M52 122c-2-5 0-10 5-13" stroke="currentColor" strokeWidth="0.8" />
    <path d="M58 90c-2-6 1-11 6-14" stroke="currentColor" strokeWidth="0.8" />
    <path d="M74 60c-1.5-5 1-10 6-12" stroke="currentColor" strokeWidth="0.8" />
    <path d="M98 36c-1-5 1-9 6-11" stroke="currentColor" strokeWidth="0.8" />
  </svg>
);

const Sparkles = () => (
  <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
    {[
      [12, 18],
      [78, 12],
      [88, 42],
      [18, 58],
      [70, 72],
      [42, 8],
      [92, 82],
      [8, 36],
    ].map(([left, top], i) => (
      <span
        key={i}
        className="absolute h-1 w-1 rounded-full bg-[#C4A36A]/45"
        style={{ left: `${left}%`, top: `${top}%` }}
      />
    ))}
  </div>
);

const BetaPage = () => {
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const [feedbackStatus, setFeedbackStatus] = useState("idle");
  const [feedbackError, setFeedbackError] = useState("");

  const onSubmit = async (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    setError("");
    setStatus("submitting");

    try {
      const res = await fetch(FORMSPREE, {
        method: "POST",
        body: new FormData(form),
        headers: { Accept: "application/json" },
      });
      if (!res.ok) throw new Error("submit");
      try {
        window.posthog?.capture?.("beta_request", { source: "beta" });
      } catch (err) {
        console.warn("Analytics unavailable", err);
      }
      setStatus("success");
    } catch {
      setStatus("idle");
      setError("We could not hold your place. Please try again.");
    }
  };

  const onFeedbackSubmit = async (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    setFeedbackError("");
    setFeedbackStatus("submitting");

    try {
      const res = await fetch(FORMSPREE, {
        method: "POST",
        body: new FormData(form),
        headers: { Accept: "application/json" },
      });
      if (!res.ok) throw new Error("submit");
      try {
        window.posthog?.capture?.("beta_feedback", { source: "beta" });
      } catch (err) {
        console.warn("Analytics unavailable", err);
      }
      setFeedbackStatus("success");
    } catch {
      setFeedbackStatus("idle");
      setFeedbackError("We could not send that. Please try again.");
    }
  };

  return (
    <div className="relative flex min-h-screen flex-col overflow-hidden bg-[#F6EFE2] font-sans text-[#1A120C]" data-testid="beta-page">
      <Sparkles />
      <CornerVine className="left-0 top-0" />
      <CornerVine className="right-0 top-0" flip />
      <CornerVine className="bottom-0 left-0 rotate-180" flip />
      <CornerVine className="bottom-0 right-0 rotate-180" />

      <header className="relative z-[1]">
        <div className="mx-auto flex w-full max-w-5xl items-center justify-end px-6 py-4 md:px-10">
          <Link
            to="/"
            className="text-[11px] font-medium uppercase tracking-[0.18em] text-[#A08448] transition-colors hover:text-[#7A5A14]"
            data-testid="beta-back-link"
          >
            Back to the site
          </Link>
        </div>
      </header>

      <main className="relative z-[1] mx-auto flex w-full max-w-[480px] flex-1 flex-col px-6 pb-16 pt-2 text-center md:pb-20">
        <img
          src="/assets/hero-crest.png?v=2"
          alt=""
          className="mx-auto h-16 w-auto sm:h-[4.5rem]"
          data-testid="beta-crest"
        />
        <p
          className="mt-4 text-[11px] font-medium uppercase tracking-[0.32em] text-[#A08448]"
          data-testid="beta-kicker"
        >
          Richmond first
        </p>
        <KickerRule />
        <h1
          className="mt-5 font-serif text-[40px] font-medium leading-[1.08] tracking-tight text-[#1A120C] md:text-[46px]"
          data-testid="beta-headline"
        >
          Join the first wave.
        </h1>
        <p
          className="mx-auto mt-4 max-w-[380px] text-[15px] leading-relaxed text-[#4A4036]"
          data-testid="beta-body"
        >
          A quiet place to meet people who actually show up. We&apos;re opening a small beta in Richmond so real people
          can try it before anyone else. All premium features are free for the test period. You&apos;ll use Expo Go.
          Things will break. Feedback is welcome.
        </p>
        <p className="mt-4 font-serif text-lg italic text-[#A08448]" data-testid="beta-start-line">
          Beta begins {BETA_START}. Everyone starts the same day.
        </p>

        {status === "success" ? (
          <CreamCard className="mt-8" testId="beta-success">
            <p className="text-[11px] font-medium uppercase tracking-[0.24em] text-[#A08448]">Confirmed</p>
            <h2 className="mt-3 font-serif text-3xl font-medium tracking-tight text-[#1A120C]">Your request is in.</h2>
            <p className="mt-4 text-[15px] leading-relaxed text-[#4A4036]">
              We&apos;ll write the day before with Expo Go instructions.
            </p>
          </CreamCard>
        ) : (
          <form
            action={FORMSPREE}
            method="POST"
            onSubmit={onSubmit}
            className="mt-8"
            data-testid="beta-form"
          >
            <input type="hidden" name="_subject" value="Community Hearts beta request" />
            <CreamCard>
              <div className="mb-5 flex items-center justify-center gap-2 text-[#C4A36A]">
                <h2 className="font-serif text-[22px] font-medium tracking-tight text-[#1A120C] sm:text-2xl">
                  Request Your Invitation
                </h2>
                <LeafSprig className="h-4 w-7" />
              </div>

              <label htmlFor="beta-email" className="sr-only">
                Email
              </label>
              <input
                id="beta-email"
                name="email"
                type="email"
                required
                autoComplete="email"
                placeholder="Your email address"
                data-testid="beta-email-input"
                className={fieldClass}
              />

              <label htmlFor="beta-city" className="sr-only">
                City
              </label>
              <div className="relative mt-3">
                <select
                  id="beta-city"
                  name="city"
                  required
                  defaultValue=""
                  data-testid="beta-city-select"
                  className={`${fieldClass} cursor-pointer appearance-none pr-10 !text-[#8A7E70] [&:has(option:checked:not([value='']))]:!text-[#1A120C]`}
                >
                  <option value="" disabled>
                    City
                  </option>
                  <option value="Richmond">Richmond</option>
                  <option value="Nearby">Nearby</option>
                  <option value="Somewhere else">Somewhere else</option>
                </select>
                <ChevronDown className="pointer-events-none absolute right-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[#A08448]" strokeWidth={1.6} />
              </div>

              <label htmlFor="beta-message" className="sr-only">
                Message (optional)
              </label>
              <textarea
                id="beta-message"
                name="message"
                rows={3}
                placeholder="Why you're excited to be part of the first wave..."
                data-testid="beta-message-input"
                className={`${fieldClass} mt-3 resize-y`}
              />

              <label
                className="mt-4 flex cursor-pointer items-start gap-2.5 text-left text-[13px] leading-snug text-[#4A4036]"
                data-testid="beta-age-label"
              >
                <input
                  type="checkbox"
                  required
                  name="age_confirm"
                  value="yes"
                  data-testid="beta-age-checkbox"
                  className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer rounded-sm accent-[#C4A36A]"
                />
                <span>I confirm that I am 18 years of age or older.</span>
              </label>

              {error ? (
                <p className="mt-3 text-sm text-[#8C5A32]" data-testid="beta-error">
                  {error}
                </p>
              ) : null}

              <div className="mt-6 flex justify-center">
                <button
                  type="submit"
                  disabled={status === "submitting"}
                  data-testid="beta-submit-button"
                  className={buttonClass}
                >
                  {status === "submitting" ? "Sending…" : "Request a beta spot"}
                  {status !== "submitting" ? <LeafSprig className="h-3.5 w-6 text-[#FFF8E8]" /> : null}
                </button>
              </div>

              <p className="mt-4 text-center text-[12px] leading-relaxed text-[#6A635A]" data-testid="beta-form-note">
                We&apos;ll email you the day before with Expo Go instructions. First wave is capped at 15 Richmond-area
                testers. Hearts+ stays free until January 1.
              </p>
            </CreamCard>
          </form>
        )}

        <FloralDivider />

        <div data-testid="beta-feedback">
          {feedbackStatus === "success" ? (
            <CreamCard testId="beta-feedback-success">
              <p className="font-serif text-xl italic text-[#A08448]">Thank you. We read every note.</p>
            </CreamCard>
          ) : (
            <form action={FORMSPREE} method="POST" onSubmit={onFeedbackSubmit} data-testid="beta-feedback-form">
              <input type="hidden" name="_subject" value="Community Hearts beta feedback" />
              <input type="hidden" name="form_type" value="beta_feedback" />
              <CreamCard>
                <p
                  className="text-[11px] font-medium uppercase tracking-[0.28em] text-[#A08448]"
                  data-testid="beta-feedback-kicker"
                >
                  Feedback
                </p>
                <h2
                  className="mt-3 font-serif text-[32px] font-medium leading-tight tracking-tight text-[#1A120C]"
                  data-testid="beta-feedback-headline"
                >
                  How&apos;s it going?
                </h2>
                <p
                  className="mx-auto mt-2 max-w-[320px] text-[15px] leading-relaxed text-[#4A4036]"
                  data-testid="beta-feedback-body"
                >
                  Tell us what you like, what&apos;s missing, or anything that feels off.
                </p>

                <label htmlFor="beta-feedback-email" className="sr-only">
                  Email (optional)
                </label>
                <input
                  id="beta-feedback-email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  placeholder="Email (optional)"
                  data-testid="beta-feedback-email-input"
                  className={`${fieldClass} mt-6 text-left`}
                />

                <label htmlFor="beta-feedback-message" className="sr-only">
                  Message
                </label>
                <textarea
                  id="beta-feedback-message"
                  name="message"
                  rows={3}
                  required
                  placeholder="Message"
                  data-testid="beta-feedback-message-input"
                  className={`${fieldClass} mt-3 resize-y text-left`}
                />

                {feedbackError ? (
                  <p className="mt-3 text-sm text-[#8C5A32]" data-testid="beta-feedback-error">
                    {feedbackError}
                  </p>
                ) : null}

                <div className="mt-6 flex justify-center">
                  <button
                    type="submit"
                    disabled={feedbackStatus === "submitting"}
                    data-testid="beta-feedback-submit-button"
                    className={buttonClass}
                  >
                    {feedbackStatus === "submitting" ? "Sending…" : "Send feedback"}
                  </button>
                </div>
              </CreamCard>
            </form>
          )}
        </div>
      </main>
    </div>
  );
};

export default BetaPage;
